import { useEffect, useState } from 'react';

// https://css-tricks.com/building-progress-ring-quickly/
export function RingProgressBar({
	strokeColor,
	color,
	radius,
	stroke,
	progress,
	text,
	className,
	inset = true,
	entrance = false,
}: {
	strokeColor: string;
	color: string;
	radius: number;
	stroke: number;
	progress: number;
	text?: string | number;
	/**
	 * Overrides the rendered size. The `viewBox` scales the drawing to match. Also where the
	 * caller supplies the negative margin that claws back the padding left by `inset`: that
	 * padding is a fraction of the rendered size, so it has to track whatever size is set here
	 * rather than being a fixed pixel value baked into this component.
	 */
	className?: string;
	/**
	 * Keeps the ring's internal padding (~11% of the box on each side) so a caller can pull
	 * neighbours into it with a negative margin. Set `false` when the ring is laid out as a
	 * plain icon and must keep its own box: the drawing is then cropped to the stroke so it
	 * fills the box edge to edge.
	 */
	inset?: boolean;
	/**
	 * Sweep the arc in from zero on mount, instead of painting the final one straight away.
	 *
	 * The sweep is a CSS transition on `stroke-dashoffset`, so it only runs when the ring has been
	 * PAINTED at an earlier offset — pass the real figure and let this own the first paint, exactly like
	 * `AnimateNumber`'s entrance mode. Pair the two: a ring next to a counting-up number must sweep, and a
	 * ring next to a static number must not, or the two read as different figures.
	 *
	 * Defaults to off, the opposite of `AnimateNumber` — most rings here render a figure that never
	 * animates (the static campaign previews), so the entrance is the exception rather than the rule.
	 * Zero on the first pass either way, so both modes hydrate deterministically.
	 */
	entrance?: boolean;
}) {
	// Two paints are required or there is no start value to interpolate from: React can commit a mount
	// effect's state update in the same paint as the mount itself, which would land the final offset with
	// nothing to transition from. A frame callback fires strictly after the zero offset has been painted.
	const [swept, setSwept] = useState(!entrance);
	useEffect(() => {
		if (!entrance) return undefined;
		const frame = requestAnimationFrame(() => setSwept(true));
		return () => cancelAnimationFrame(frame);
	}, [entrance]);

	const normalizedRadius = radius - stroke * 2;
	const circumference = normalizedRadius * 2 * Math.PI;
	const strokeDashoffset = circumference - ((swept ? progress : 0) / 100) * circumference;

	// Outer edge of the painted stroke, measured from the centre.
	const painted = normalizedRadius + stroke / 2;
	const viewBox = inset
		? `0 0 ${radius * 2} ${radius * 2}`
		: `${radius - painted} ${radius - painted} ${painted * 2} ${painted * 2}`;

	return (
		<svg height={radius * 2} width={radius * 2} viewBox={viewBox} className={className}>
			{/* Placeholder */}
			<circle
				stroke={strokeColor}
				fill="transparent"
				className="opacity-35"
				strokeWidth={stroke}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			{/* Bar */}
			<circle
				className="-rotate-90 transition-[stroke-dashoffset] duration-600"
				stroke={color}
				fill="transparent"
				strokeWidth={stroke}
				strokeDasharray={`${circumference} ${circumference}`}
				// The pivot must be the circle's own centre in user units. `origin-center`
				// resolves against the viewBox, so it silently moves the arc out of view
				// whenever the viewBox is not `0 0 2r 2r` (see the cropped `inset={false}` case).
				style={{ strokeDashoffset, transformOrigin: `${radius}px ${radius}px` }}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			{text != null && (
				<text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="22" fill="rgb(var(--text))">
					{text}
				</text>
			)}
		</svg>
	);
}
